import { useParams, useNavigate } from "react-router-dom";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock } from "lucide-react";

// Explicit titles for slugs that can't be cleanly humanized
const SECTION_TITLES: Record<string, string> = {
  "games-schedule":    "Games & Schedule",
  "teams-rosters":     "Teams & Rosters",
  "age-verification":  "Age Verification",
  "fitness-tests":     "Fitness Tests",
  "performance-stats": "Performance Stats",
};

function humanizeSection(slug: string): string {
  if (SECTION_TITLES[slug]) return SECTION_TITLES[slug];
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Per-section description hints — falls back to generic
const SECTION_DESCRIPTIONS: Record<string, string> = {
  "games-schedule":    "Manage match fixtures, ground allocations, and umpire assignments across all tournament days.",
  "grounds":           "Track ground availability, surface types, and capacity for each venue in your hub.",
  "teams-rosters":     "View and manage team rosters, player eligibility, and squad submissions across all academies.",
  "age-verification":  "Verify player age documentation and flag discrepancies before tournament registration closes.",
  "fitness-tests":     "Record and review fitness trial results (Yo-Yo, sprint, shuttle) tied to selection eligibility.",
  "performance-stats": "Aggregated batting, bowling, and fielding statistics across all tournaments in this hub.",
  "announcements":     "Broadcast updates to all registered academies and team managers in one place.",
  "reports":           "Generate and export hub-level reports: registration summaries, payment status, and selection outcomes.",
};

export default function HubComingSoonStub() {
  const { section = "this-section" } = useParams<{ section: string }>();
  const navigate = useNavigate();
  const title = humanizeSection(section);
  const description =
    SECTION_DESCRIPTIONS[section] ??
    "This section will be available in a future release of the Hub Admin platform.";

  return (
    <HubAdminDemoLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 py-16 text-center" data-testid="hub-coming-soon-page">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-5">
          <Clock className="h-8 w-8 text-gray-400" />
        </div>

        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Coming Soon</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-3" data-testid="coming-soon-title">{title}</h1>
        <p className="text-gray-500 max-w-sm text-sm leading-relaxed mb-8">{description}</p>

        <Button
          variant="outline"
          onClick={() => navigate("/hub/dashboard-demo")}
          data-testid="btn-back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    </HubAdminDemoLayout>
  );
}
